import React, { useState, useMemo, useCallback, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
    Drawer, Box, Typography, Stack, Divider,
    IconButton, Button, Checkbox, Paper,
    Tooltip, Chip, Collapse,
} from '@mui/material';
import {
    Close as CloseIcon,
    PictureAsPdf as PdfIcon,
    Assignment as ReportIcon,
    FlashOn as PresentIcon,
    AutoGraph as NextIcon,
    ReportProblem as IssueIcon,
    EventNote as AobIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    SelectAll as SelectAllIcon,
    CheckBoxOutlineBlank as NoneIcon,
} from '@mui/icons-material';

// ── Constants ─────────────────────────────────────────────────────────────────

const ALL_SECTIONS = ['presentWeek', 'nextWeek', 'issues', 'aob'];

const SECTION_META = {
    presentWeek: { label: 'Present Week',       Icon: PresentIcon, color: '#1976d2' },
    nextWeek:    { label: 'Next Week',           Icon: NextIcon,    color: '#7b1fa2' },
    issues:      { label: 'Issues & Risks',      Icon: IssueIcon,   color: '#d32f2f' },
    aob:         { label: 'Any Other Business',  Icon: AobIcon,     color: '#546e7a' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const getItems = (sub, section) => {
    if (section === 'presentWeek') return sub.presentWeekActivities ?? [];
    if (section === 'nextWeek')    return sub.nextWeekActivities    ?? [];
    if (section === 'issues')      return sub.issues                ?? [];
    if (section === 'aob')         return sub.aob                   ?? [];
    return [];
};

const buildInitialConfig = (submissions) =>
    Object.fromEntries(
        submissions.map((sub) => [
            sub.userId,
            {
                included: true,
                items: Object.fromEntries(
                    ALL_SECTIONS.map((s) => [s, getItems(sub, s).map(() => true)])
                ),
            },
        ])
    );

// ── Print CSS ─────────────────────────────────────────────────────────────────

const PRINT_CSS = `
  /* ── Reset ── */
  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Page ── */
  body {
    font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
    font-size: 12px;
    color: #1a1a2e;
    background: #fff;
    width: 960px;
  }

  /* ── Document header band ── */
  .doc-header {
    background: linear-gradient(135deg, #0d47a1 0%, #1976d2 60%, #42a5f5 100%);
    padding: 36px 48px 28px;
    color: #fff;
    position: relative;
    overflow: hidden;
  }
  .doc-header::after {
    content: '';
    position: absolute;
    right: -40px; top: -40px;
    width: 220px; height: 220px;
    border-radius: 50%;
    background: rgba(255,255,255,0.06);
  }
  .doc-header::before {
    content: '';
    position: absolute;
    right: 60px; bottom: -60px;
    width: 160px; height: 160px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }
  .doc-header-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    opacity: 0.7;
    margin-bottom: 10px;
  }
  .doc-header-title {
    font-size: 28px;
    font-weight: 900;
    letter-spacing: -0.5px;
    margin-bottom: 6px;
  }
  .doc-header-meta {
    font-size: 12px;
    opacity: 0.75;
    display: flex;
    gap: 24px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255,255,255,0.2);
  }
  .doc-header-meta span { display: flex; align-items: center; gap: 6px; }
  .doc-header-meta strong { opacity: 1; font-weight: 700; }

  /* ── Content wrapper ── */
  .content { padding: 32px 48px 48px; }

  /* ── Member block ── */
  .member-block {
    margin-bottom: 36px;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06);
    page-break-inside: avoid;
  }

  /* ── Member header ── */
  .member-header {
    background: #1565c0;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .member-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 900; color: #fff;
    flex-shrink: 0;
    margin-right: 12px;
  }
  .member-info { flex: 1; }
  .member-name {
    font-size: 14px; font-weight: 800; color: #fff;
    letter-spacing: 0.2px;
  }
  .member-email { font-size: 10px; color: rgba(255,255,255,0.65); margin-top: 1px; }

  /* ── Sections grid ── */
  .sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: #fff;
  }
  .section {
    padding: 18px 20px;
    border-right: 1px solid #e8edf3;
    border-top: 1px solid #e8edf3;
  }
  .section:nth-child(even) { border-right: none; }
  .section.full-width { grid-column: 1 / -1; border-right: none; }

  /* ── Section title ── */
  .section-label {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e3f2fd;
  }
  .section-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .section-dot.present  { background: #1976d2; }
  .section-dot.next     { background: #7b1fa2; }
  .section-dot.issues   { background: #c62828; }
  .section-dot.aob      { background: #546e7a; }
  .section-title {
    font-size: 9px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #455a64;
  }
  .section-title.issues { color: #c62828; }

  /* ── Activity card ── */
  .activity {
    background: #f5f9ff;
    border: 1px solid #dbeafe;
    border-left: 3px solid #1976d2;
    border-radius: 6px;
    padding: 9px 12px;
    margin-bottom: 8px;
  }
  .activity:last-child { margin-bottom: 0; }
  .activity-meta {
    font-size: 9px;
    font-weight: 800;
    color: #1565c0;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 4px;
  }
  .activity-desc {
    font-size: 11.5px;
    line-height: 1.55;
    color: #263238;
  }

  /* ── Issue item ── */
  .text-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 7px 10px;
    margin-bottom: 6px;
    border-radius: 5px;
    background: #fafafa;
    border: 1px solid #ececec;
    font-size: 11.5px;
    line-height: 1.55;
    color: #263238;
  }
  .text-item:last-child { margin-bottom: 0; }
  .text-item-bullet {
    width: 6px; height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 5px;
  }
  .text-item.issue { background: #fff5f5; border-color: #ffcdd2; }
  .text-item.issue .text-item-bullet { background: #c62828; }
  .text-item.aob .text-item-bullet { background: #546e7a; }

  /* ── Empty state ── */
  .empty { color: #b0bec5; font-style: italic; font-size: 11px; }

  /* footer drawn directly by jsPDF — see addFooterToAllPages */
`;

// ── Report HTML builder ───────────────────────────────────────────────────────

// ── Shared HTML helpers ───────────────────────────────────────────────────────

// ── Sort activities by Tech → Vendor (case-insensitive) ─────────────────────
const sortActivities = (list) =>
    [...list].sort((a, b) => {
        const t = (a.technology || '').localeCompare(b.technology || '');
        if (t !== 0) return t;
        return (a.vendor || '').localeCompare(b.vendor || '');
    });

// ── Collect & filter items from all included submissions for a given section ──
const collectSection = (included, config, section) => {
    const items = [];
    for (const sub of included) {
        const cfg  = config[sub.userId];
        const list = (() => {
            if (section === 'presentWeek') return sub.presentWeekActivities ?? [];
            if (section === 'nextWeek')    return sub.nextWeekActivities    ?? [];
            if (section === 'issues')      return sub.issues                ?? [];
            if (section === 'aob')         return sub.aob                   ?? [];
            return [];
        })();
        list.forEach((item, i) => {
            if (cfg.items[section][i]) items.push(item);
        });
    }
    return items;
};

// ── HTML helpers ──────────────────────────────────────────────────────────────
const actHtml = (list) =>
    list.length === 0
        ? '<div class="empty">No entries</div>'
        : list.map((a) => `
            <div class="activity">
                <div class="activity-meta">${a.technology || ''}${a.technology && a.vendor ? ' &nbsp;·&nbsp; ' : ''}${a.vendor || ''}</div>
                <div class="activity-desc">${a.description || ''}</div>
            </div>`).join('');

const txtHtml = (list, cls) =>
    list.length === 0
        ? '<div class="empty">No entries</div>'
        : list.map((t) => `
            <div class="text-item ${cls}">
                <div class="text-item-bullet"></div>
                <div>${t.description || ''}</div>
            </div>`).join('');

const sectionHtml = (dotCls, titleCls, label, inner, fullWidth) => `
    <div class="section${fullWidth ? ' full-width' : ''}">
        <div class="section-label">
            <div class="section-dot ${dotCls}"></div>
            <div class="section-title ${titleCls}">${label}</div>
        </div>
        ${inner}
    </div>`;

// ── Build HTML for one thematic section block ─────────────────────────────────
// Each section is rendered as a standalone document so html2canvas captures it
// cleanly and addCanvasToPdf decides pagination without cutting content.
const buildSectionBlockHtml = (dotCls, titleCls, label, inner, fullWidth) => {
    const block = sectionHtml(dotCls, titleCls, label, inner, fullWidth);
    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
        <style>${PRINT_CSS}
        body { width:900px; padding:16px 0; background:#fff; }
        .section { border-top: none; }
        </style>
        </head><body>${block}</body></html>`;
};

// ── Build document header HTML ────────────────────────────────────────────────
const buildDocHeaderHtml = (weekNumber, year, memberCount) => {
    const now = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
        <style>${PRINT_CSS} body { width:900px; padding:0; background:#fff; }</style>
        </head><body>
        <div class="doc-header">
            <div class="doc-header-label">Internal Report</div>
            <div class="doc-header-title">Weekly Update — Week ${weekNumber} / ${year}</div>
            <div class="doc-header-meta">
                <span><strong>Generated</strong> ${now}</span>
                <span><strong>Members</strong> ${memberCount}</span>
            </div>
        </div>
        </body></html>`;
};

// ── SectionPanel ──────────────────────────────────────────────────────────────
// No onClick on the row container — each interactive element handles its own click.

const SectionPanel = ({ section, items, checkedItems, onToggleItem, onToggleAll }) => {
    const [open, setOpen] = useState(true);
    const { label, Icon, color } = SECTION_META[section];
    const allOn  = checkedItems.every(Boolean);
    const someOn = checkedItems.some(Boolean);

    if (items.length === 0) return null;

    return (
        <Box sx={{ mb: 1 }}>
            {/* Section header — two separate click zones */}
            <Stack direction="row" alignItems="center" justifyContent="space-between"
                sx={{ px: 1.5, py: 0.7, bgcolor: 'grey.50', borderRadius: 1 }}
            >
                {/* LEFT: checkbox + label — clicking anywhere here toggles select-all */}
                <Stack direction="row" alignItems="center" spacing={1}
                    onClick={() => onToggleAll(!allOn)}
                    sx={{ cursor: 'pointer', flex: 1 }}
                >
                    <Checkbox
                        size="small"
                        checked={allOn}
                        indeterminate={someOn && !allOn}
                        onChange={() => {}} // controlled by parent onClick
                        sx={{ p: 0.3, pointerEvents: 'none' }} // checkbox is visual only
                    />
                    <Icon sx={{ fontSize: 14, color }} />
                    <Typography variant="caption" fontWeight={800} sx={{ color, letterSpacing: 0.5 }}>
                        {label}
                    </Typography>
                    <Chip
                        label={`${checkedItems.filter(Boolean).length} / ${items.length}`}
                        size="small"
                        sx={{
                            height: 16, fontSize: 10, fontWeight: 700,
                            bgcolor: !someOn ? 'grey.200' : 'primary.50',
                            color:   !someOn ? 'text.disabled' : 'primary.main',
                        }}
                    />
                </Stack>

                {/* RIGHT: expand/collapse — separate zone */}
                <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ p: 0.3 }}>
                    {open ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                </IconButton>
            </Stack>

            {/* Per-item list */}
            <Collapse in={open}>
                <Box sx={{ pl: 3.5, pt: 0.5, pb: 0.5 }}>
                    {items.map((item, i) => (
                        <Stack key={i} direction="row" alignItems="flex-start" spacing={0.5}
                            onClick={() => onToggleItem(i)}
                            sx={{
                                py: 0.5, px: 0.8, mb: 0.3, borderRadius: 1,
                                opacity: checkedItems[i] ? 1 : 0.35,
                                bgcolor: checkedItems[i] ? 'transparent' : 'grey.50',
                                '&:hover': { bgcolor: 'action.hover' },
                                cursor: 'pointer',
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={!!checkedItems[i]}
                                onChange={() => {}} // controlled by row onClick
                                sx={{ p: 0.2, flexShrink: 0, mt: 0.15, pointerEvents: 'none' }}
                            />
                            <Typography variant="caption" sx={{ lineHeight: 1.6, wordBreak: 'break-word' }}>
                                {(section === 'presentWeek' || section === 'nextWeek') && (item.technology || item.vendor) && (
                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
                                        {[item.technology, item.vendor].filter(Boolean).join(' • ')}
                                        {' — '}
                                    </Box>
                                )}
                                {item.description || <em style={{ color: '#bbb' }}>empty</em>}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

// ── UserRow ───────────────────────────────────────────────────────────────────
// Checkbox and expand/collapse are separate click zones — no event propagation needed.

const UserRow = ({ sub, cfg, isOpen, onToggleUser, onToggleExpand, onToggleItem, onToggleSectionAll, onSelectAllItems }) => {
    const included = cfg.included;
    const userSelected = ALL_SECTIONS.reduce((sum, s) => sum + cfg.items[s].filter(Boolean).length, 0);
    const userTotal    = ALL_SECTIONS.reduce((sum, s) => sum + getItems(sub, s).length, 0);

    return (
        <Paper elevation={0} sx={{
            borderRadius: 2,
            border: '1.5px solid',
            borderColor: included ? 'primary.main' : 'divider',
            opacity: included ? 1 : 0.5,
            transition: 'all 0.15s',
            overflow: 'hidden',
        }}>
            {/* Header row — two independent click zones */}
            <Stack direction="row" alignItems="center"
                sx={{
                    bgcolor: included ? 'primary.50' : 'grey.50',
                    borderBottom: included && isOpen ? '1px solid' : 'none',
                    borderColor: 'divider',
                }}
            >
                {/* ZONE A: checkbox — toggles include/exclude */}
                <Stack direction="row" alignItems="center" spacing={1}
                    onClick={onToggleUser}
                    sx={{ px: 2, py: 1.2, cursor: 'pointer', flex: 1, minWidth: 0 }}
                >
                    <Checkbox
                        size="small"
                        checked={included}
                        onChange={() => {}} // controlled by zone onClick
                        sx={{ p: 0.3, flexShrink: 0, pointerEvents: 'none' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
                            {sub.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {sub.userEmail}
                        </Typography>
                    </Box>
                </Stack>

                {/* ZONE B: select-all items for this user */}
                {included && (
                    <Button
                        size="small"
                        startIcon={<SelectAllIcon sx={{ fontSize: 16 }} />}
                        onClick={onSelectAllItems}
                        sx={{ mx: 0.5, fontSize: 11, fontWeight: 700, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}
                    >
                        Select all
                    </Button>
                )}

                {/* ZONE C: counter + expand — toggles collapse */}
                <Stack direction="row" alignItems="center" spacing={1}
                    onClick={onToggleExpand}
                    sx={{ pr: 2, py: 1.2, cursor: 'pointer', flexShrink: 0 }}
                >
                    <Chip
                        label={`${userSelected} / ${userTotal}`}
                        size="small"
                        color={userSelected === 0 ? 'default' : 'primary'}
                        variant={userSelected === 0 ? 'outlined' : 'filled'}
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, pointerEvents: 'none' }}
                    />
                    {isOpen
                        ? <CollapseIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                        : <ExpandIcon   sx={{ fontSize: 18, color: 'text.disabled' }} />
                    }
                </Stack>
            </Stack>

            {/* Section panels */}
            {included && (
                <Collapse in={isOpen}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        {ALL_SECTIONS.map((section) => {
                            const items = getItems(sub, section);
                            if (items.length === 0) return null;
                            return (
                                <SectionPanel
                                    key={section}
                                    section={section}
                                    items={items}
                                    checkedItems={cfg.items[section]}
                                    onToggleItem={(i) => onToggleItem(section, i)}
                                    onToggleAll={(val) => onToggleSectionAll(section, val)}
                                />
                            );
                        })}
                    </Box>
                </Collapse>
            )}
        </Paper>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const ReportBuilder = ({ open, submissions, weekNumber, year, onClose }) => {
    const [config,   setConfig]   = useState(() => buildInitialConfig(submissions));
    const [expanded, setExpanded] = useState({});
    const [exporting, setExporting] = useState(false);

    const submissionsKey = submissions.map((s) => s.userId).join(',');
    useEffect(() => {
        setConfig(buildInitialConfig(submissions));
        setExpanded({});
    }, [submissionsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Setters ───────────────────────────────────────────────────────────────

    const toggleUser = useCallback((userId) => {
        setConfig((prev) => {
            const u = prev[userId];
            if (!u) return prev;
            return { ...prev, [userId]: { ...u, included: !u.included } };
        });
    }, []);

    const toggleExpand = useCallback((userId) => {
        setExpanded((prev) => ({ ...prev, [userId]: !(prev[userId] !== false) }));
    }, []);

    const toggleItem = useCallback((userId, section, index) => {
        setConfig((prev) => {
            const u = prev[userId];
            if (!u) return prev;
            const arr = [...u.items[section]];
            arr[index] = !arr[index];
            return { ...prev, [userId]: { ...u, items: { ...u.items, [section]: arr } } };
        });
    }, []);

    const toggleSectionAll = useCallback((userId, section, value) => {
        setConfig((prev) => {
            const u = prev[userId];
            if (!u) return prev;
            return {
                ...prev,
                [userId]: { ...u, items: { ...u.items, [section]: u.items[section].map(() => value) } },
            };
        });
    }, []);

    const toggleAllUsers = useCallback((include) => {
        setConfig((prev) =>
            Object.fromEntries(Object.entries(prev).map(([uid, cfg]) => [uid, { ...cfg, included: include }]))
        );
    }, []);

    const selectAllItems = useCallback(() => {
        setConfig((prev) =>
            Object.fromEntries(
                Object.entries(prev).map(([uid, cfg]) => [
                    uid,
                    {
                        included: true,
                        items: Object.fromEntries(ALL_SECTIONS.map((s) => [s, cfg.items[s].map(() => true)])),
                    },
                ])
            )
        );
    }, []);

    const selectUserItems = useCallback((userId) => {
        setConfig((prev) => {
            const u = prev[userId];
            if (!u) return prev;
            return {
                ...prev,
                [userId]: {
                    ...u,
                    included: true,
                    items: Object.fromEntries(ALL_SECTIONS.map((s) => [s, u.items[s].map(() => true)])),
                },
            };
        });
    }, []);

    // ── Stats ─────────────────────────────────────────────────────────────────

    const { includedUsers, totalSelected } = useMemo(() => {
        let users = 0, items = 0;
        Object.values(config).forEach((cfg) => {
            if (!cfg.included) return;
            users++;
            ALL_SECTIONS.forEach((s) => { items += cfg.items[s].filter(Boolean).length; });
        });
        return { includedUsers: users, totalSelected: items };
    }, [config]);

    // ── Export PDF ────────────────────────────────────────────────────────────

    const handleExportPdf = async () => {
        setExporting(true);
        let iframe = null;

        // Renders an HTML string into a hidden iframe and returns an html2canvas capture
        const renderToCanvas = async (html, width = 900) => {
            iframe = document.createElement('iframe');
            iframe.style.cssText = `position:absolute;top:-99999px;left:-99999px;width:${width}px;height:1px;border:none;opacity:0;pointer-events:none;`;
            document.body.appendChild(iframe);
            await new Promise((resolve, reject) => {
                iframe.onload  = resolve;
                iframe.onerror = reject;
                iframe.srcdoc  = html;
            });
            await new Promise((r) => setTimeout(r, 350));
            const doc  = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) throw new Error('iframe not accessible');
            const body = doc.body;
            const fullH = Math.max(body.scrollHeight, body.offsetHeight, 10);
            iframe.style.height = fullH + 'px';
            await new Promise((r) => setTimeout(r, 80));
            const canvas = await html2canvas(body, {
                scale: 2, useCORS: true, backgroundColor: '#ffffff',
                windowWidth: width, scrollY: 0,
            });
            document.body.removeChild(iframe);
            iframe = null;
            return canvas;
        };

        // Adds a canvas to the PDF. If it doesn't fit on the current page,
        // creates a new page first. Returns the new yOffset after placing the image.
        const addCanvasToPdf = (pdf, canvas, yOffset, margin, usableW, usableH) => {
            const ratio   = canvas.width / usableW;
            const imgH    = canvas.height / ratio;
            let y = yOffset;

            // If there's already content on the page and this block won't fit → new page
            if (y > margin && y + imgH > usableH + margin) {
                pdf.addPage();
                y = margin;
            }

            // Block fits on one page → place it directly
            if (imgH <= usableH) {
                pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, y, usableW, imgH);
                return y + imgH + 6; // 6mm gap between members
            }

            // Block is taller than one page → slice it across pages
            let srcY = 0;
            while (srcY < canvas.height) {
                if (srcY > 0) { pdf.addPage(); y = margin; }
                const sliceH   = Math.min(usableH - (y - margin), (canvas.height - srcY) / ratio);
                const srcH     = Math.max(1, Math.round(sliceH * ratio));
                const sc       = document.createElement('canvas');
                sc.width       = canvas.width;
                sc.height      = srcH;
                sc.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
                pdf.addImage(sc.toDataURL('image/jpeg', 0.95), 'JPEG', margin, y, usableW, sliceH);
                srcY += srcH;
                y    += sliceH;
            }
            return y + 6;
        };

        try {
            const pdf     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW   = pdf.internal.pageSize.getWidth();
            const pageH   = pdf.internal.pageSize.getHeight();
            const margin  = 12;
            const usableW = pageW - margin * 2;
            const usableH = pageH - margin * 2;

            // ── 1. Document header (first page top) ────────────────────────
            const included = submissions.filter((s) => config[s.userId]?.included);
            const headerCanvas = await renderToCanvas(buildDocHeaderHtml(weekNumber, year, included.length));
            const headerH = headerCanvas.height / (headerCanvas.width / usableW);
            pdf.addImage(headerCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, usableW, headerH);
            let yOffset = margin + headerH + 8;

            // ── 2. One thematic section at a time, sorted by Tech → Vendor ──
            const SECTIONS = [
                { key: 'presentWeek', dotCls: 'present', titleCls: '',       label: 'Present Week'       },
                { key: 'nextWeek',    dotCls: 'next',    titleCls: '',       label: 'Next Week'          },
                { key: 'issues',      dotCls: 'issues',  titleCls: 'issues', label: 'Issues & Risks'     },
                { key: 'aob',         dotCls: 'aob',     titleCls: '',       label: 'Any Other Business' },
            ];

            for (const sec of SECTIONS) {
                let items = collectSection(included, config, sec.key);
                if (!items.length) continue;

                // Sort activities by Tech → Vendor; text items keep insertion order
                if (sec.key === 'presentWeek' || sec.key === 'nextWeek')
                    items = sortActivities(items);

                const inner    = sec.key === 'issues' ? txtHtml(items, 'issue')
                               : sec.key === 'aob'    ? txtHtml(items, 'aob')
                               : actHtml(items);
                const fullW    = sec.key === 'issues' || sec.key === 'aob';
                const html     = buildSectionBlockHtml(sec.dotCls, sec.titleCls, sec.label, inner, fullW);
                const canvas   = await renderToCanvas(html);
                yOffset        = addCanvasToPdf(pdf, canvas, yOffset, margin, usableW, usableH);
            }

            // ── Draw footer on every page (fixed position at page bottom) ──────
            const totalPages = pdf.internal.getNumberOfPages();
            const footerY    = pageH - 8; // 8mm from bottom edge
            for (let p = 1; p <= totalPages; p++) {
                pdf.setPage(p);
                // Thin separator line
                pdf.setDrawColor(195, 214, 235);
                pdf.setLineWidth(0.3);
                pdf.line(margin, footerY - 4, pageW - margin, footerY - 4);
                // Left text
                pdf.setFontSize(7);
                pdf.setTextColor(160, 170, 180);
                pdf.text('Confidential — Internal Use Only', margin, footerY);
                // Centre: page number
                pdf.setTextColor(160, 170, 180);
                pdf.text(`Page ${p} of ${totalPages}`, pageW / 2, footerY, { align: 'center' });
                // Right text
                pdf.setTextColor(25, 118, 210);
                pdf.setFont(undefined, 'bold');
                pdf.text('Weekly Update Report', pageW - margin, footerY, { align: 'right' });
                pdf.setFont(undefined, 'normal');
            }

            pdf.save(`WeeklyUpdate_W${weekNumber}_${year}.pdf`);
        } catch (err) {
            console.error('PDF export failed:', err);
        } finally {
            if (iframe && document.body.contains(iframe)) document.body.removeChild(iframe);
            setExporting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', md: 820, lg: 980 }, display: 'flex', flexDirection: 'column' } }}
        >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ReportIcon color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight={800}>Report Builder</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Week {weekNumber} / {year} &bull; {includedUsers} user(s) &bull; {totalSelected} item(s) selected
                        </Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>

            {/* Toolbar */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"
                sx={{ px: 3, py: 1.2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">Users:</Typography>
                <Chip icon={<SelectAllIcon sx={{ fontSize: 14 }} />} label="All" size="small"
                    variant="outlined" onClick={() => toggleAllUsers(true)} sx={{ cursor: 'pointer', fontWeight: 700 }} />
                <Chip icon={<NoneIcon sx={{ fontSize: 14 }} />} label="None" size="small"
                    variant="outlined" onClick={() => toggleAllUsers(false)} sx={{ cursor: 'pointer', fontWeight: 700 }} />
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography variant="caption" color="text.secondary">Items:</Typography>
                <Chip icon={<SelectAllIcon sx={{ fontSize: 14 }} />} label="Select all" size="small"
                    variant="outlined" color="primary" onClick={selectAllItems} sx={{ cursor: 'pointer', fontWeight: 700 }} />
            </Stack>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                {submissions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                        No submissions loaded. Select a week first.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {submissions.map((sub) => {
                            const cfg = config[sub.userId];
                            if (!cfg) return null;
                            const isOpen = expanded[sub.userId] !== false;
                            return (
                                <UserRow
                                    key={sub.userId}
                                    sub={sub}
                                    cfg={cfg}
                                    isOpen={isOpen}
                                    onToggleUser={() => toggleUser(sub.userId)}
                                    onToggleExpand={() => toggleExpand(sub.userId)}
                                    onToggleItem={(section, i) => toggleItem(sub.userId, section, i)}
                                    onToggleSectionAll={(section, val) => toggleSectionAll(sub.userId, section, val)}
                                    onSelectAllItems={() => selectUserItems(sub.userId)}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Footer */}
            <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">
                    {includedUsers} user(s) &bull; {totalSelected} item(s) selected
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button onClick={onClose}>Close</Button>
                    <Tooltip title={totalSelected === 0 ? 'Select at least one item' : ''}>
                        <span>
                            <Button variant="contained" startIcon={exporting ? null : <PdfIcon />}
                                onClick={handleExportPdf} disabled={totalSelected === 0 || exporting}>
                                {exporting ? 'Exporting...' : 'Export PDF'}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>
        </Drawer>
    );
};

export default ReportBuilder;